import { isoDateInTimeZone } from '@multaqa/i18n'

/**
 * The period and warehouse filter every management dashboard shares, kept in
 * the route's URL search params.
 *
 * Presets resolve to `period_from` / `period_to` here, as calendar-date
 * strings only. Nothing is parsed as a UTC instant: every step works on
 * year/month/day numbers, so a reader east of Greenwich never gets yesterday.
 */

export const DASHBOARD_PRESETS = [
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'custom',
] as const

export type DashboardPreset = (typeof DASHBOARD_PRESETS)[number]

export interface DashboardSearch {
  period?: DashboardPreset
  /** Custom range only. */
  from?: string
  /** Custom range only. */
  to?: string
  warehouse?: string
}

export interface DashboardPeriod {
  from: string
  to: string
}

/** The API refuses a range longer than this, counting both ends. */
export const DASHBOARD_MAX_DAYS = 366

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const DAY_MS = 86_400_000

type Ymd = { year: number; month: number; day: number }

function parseIsoDate(value: string): Ymd | null {
  const match = ISO_DATE.exec(value)
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const probe = new Date(Date.UTC(year, month - 1, day))
  const real =
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  return real ? { year, month, day } : null
}

/** Normalises overflow (month 0, day 0) through `Date.UTC`, then prints the date. */
function isoDate(year: number, month: number, day: number): string {
  const date = new Date(Date.UTC(year, month - 1, day))
  const yyyy = String(date.getUTCFullYear()).padStart(4, '0')
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(date.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function dayNumber({ year, month, day }: Ymd): number {
  return Date.UTC(year, month - 1, day) / DAY_MS
}

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined)
const date = (value: unknown) => {
  const raw = text(value)
  return raw && parseIsoDate(raw) ? raw : undefined
}

export function dashboardSearchFrom(search: Record<string, unknown>): DashboardSearch {
  return {
    period: DASHBOARD_PRESETS.find((preset) => preset === search.period),
    from: date(search.from),
    to: date(search.to),
    warehouse: text(search.warehouse),
  }
}

/**
 * Today as the API measures it. The API has no company timezone and checks
 * `period_to <= today` in the application timezone, UTC; a browser-local
 * "today" would be refused with a 422 for a few hours after local midnight
 * anywhere east of UTC.
 */
export function dashboardToday(now: Date = new Date()): string {
  return isoDateInTimeZone(now, 'UTC')
}

function presetPeriod(preset: Exclude<DashboardPreset, 'custom'>, today: Ymd): DashboardPeriod {
  const end = isoDate(today.year, today.month, today.day)
  switch (preset) {
    case 'last_month':
      return {
        from: isoDate(today.year, today.month - 1, 1),
        to: isoDate(today.year, today.month, 0),
      }
    case 'this_quarter':
      return { from: isoDate(today.year, Math.floor((today.month - 1) / 3) * 3 + 1, 1), to: end }
    case 'this_year':
      return { from: isoDate(today.year, 1, 1), to: end }
    default:
      return { from: isoDate(today.year, today.month, 1), to: end }
  }
}

/**
 * The dates to ask the API for, or `null` when a custom range is incomplete,
 * backwards, ends after today or is longer than the API accepts. The screen
 * says so rather than sending a request it knows will be refused.
 */
export function resolveDashboardPeriod(
  search: DashboardSearch,
  today: string,
): DashboardPeriod | null {
  const todayParts = parseIsoDate(today)
  if (!todayParts) return null

  const preset = search.period ?? 'this_month'
  if (preset !== 'custom') return presetPeriod(preset, todayParts)

  const from = search.from ? parseIsoDate(search.from) : null
  const to = search.to ? parseIsoDate(search.to) : null
  if (!from || !to || !search.from || !search.to) return null

  const start = dayNumber(from)
  const end = dayNumber(to)
  if (start > end || end > dayNumber(todayParts)) return null
  if (end - start + 1 > DASHBOARD_MAX_DAYS) return null

  return { from: search.from, to: search.to }
}

/** True when nothing differs from the default: this month, every warehouse. */
export function isDefaultDashboardSearch(search: DashboardSearch): boolean {
  return (search.period ?? 'this_month') === 'this_month' && !search.warehouse
}

/** Every shared filter key cleared: what the reset action writes. */
export const CLEARED_DASHBOARD_FILTERS: Required<Record<keyof DashboardSearch, undefined>> = {
  period: undefined,
  from: undefined,
  to: undefined,
  warehouse: undefined,
}

export type DashboardPeriodProblem = 'incomplete' | 'out_of_range'

/** Why `resolveDashboardPeriod` refused the range, so the filter bar can say so. */
export function dashboardPeriodProblem(
  search: DashboardSearch,
  today: string,
): DashboardPeriodProblem | null {
  if (resolveDashboardPeriod(search, today) !== null) return null
  return !search.from || !search.to ? 'incomplete' : 'out_of_range'
}

/** The earlier of two ISO dates; ISO strings sort as dates. */
export function earlierIsoDate(a: string | undefined, b: string): string {
  return a !== undefined && a < b ? a : b
}
